// Invoice HTML template — drives both on-screen rendering (iframe srcDoc on
// the InvoiceDetail pages) and the Puppeteer PDF generator. Pure JS string
// concatenation; no React, no Tailwind. Inline styles only so it survives
// embedding in any context.
//
// Brand colors:
//   Primary purple  #9B50E0
//   Lime accent     #B0FC38
//   Navy ink        #0a1228
//
// Logo is the base64-encoded PNG from public/assets/images/nexsav logo.png
// (kept in _invoice-logo-base64.js so this file stays diff-friendly).

import { NEXSAV_LOGO_PNG_DATA_URL } from './_invoice-logo-base64.js';

const VENDOR_NUMBER = 'NEX833087';
const COMPANY_NAME = 'NexSav Innovate, LLC';
const COMPANY_TAGLINE = `NYC DOE Vendor #${VENDOR_NUMBER}`;
// Business address shown on every invoice (issuer block + footer). Added
// 2026-09-28 at Ammar's request; one source of truth for the document.
export const COMPANY_ADDRESS_LINES = ['4747 197th Street', 'Flushing, NY 11358'];
export const COMPANY_ADDRESS_INLINE = COMPANY_ADDRESS_LINES.join(', ');

const NYC_DOE_TAX_EXEMPT_NOTE =
  'New York City public schools are exempt from sales tax under DOE policy. ' +
  'No sales tax is charged on this invoice.';

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function fmtMoney(cents, currency = 'USD') {
  if (cents == null) return '—';
  return (Number(cents) / 100).toLocaleString('en-US', { style: 'currency', currency });
}

function fmtDate(s) {
  if (!s) return '—';
  return String(s).slice(0, 10);
}

function fmtTaxRate(invoice) {
  if (invoice.tax_exempt === 1) return 'EXEMPT';
  if (invoice.tax_rate_basis_points == null) return '—';
  return `${(Number(invoice.tax_rate_basis_points) / 100).toFixed(2)}%`;
}

function fmtTaxAmount(invoice) {
  if (invoice.tax_exempt === 1) return 'EXEMPT';
  return fmtMoney(invoice.tax_amount_cents);
}

function billedToBlock(account) {
  if (!account?.name) return '<div style="color:#999;">—</div>';
  const lines = [`<div style="font-size:13px;font-weight:600;color:#0a1228;">${escapeHtml(account.name)}</div>`];
  const subhead = [];
  if (account.type_school_dbn) subhead.push(`DBN ${escapeHtml(account.type_school_dbn)}`);
  if (subhead.length) lines.push(`<div style="font-size:10px;color:#9aa5b1;letter-spacing:0.04em;text-transform:uppercase;margin-top:2px;">${subhead.join(' · ')}</div>`);
  const addr = [account.type_school_address, account.type_school_city, account.type_school_zip].filter(Boolean).join(', ');
  if (addr) lines.push(`<div style="margin-top:6px;color:#475569;font-size:11px;">${escapeHtml(addr)}</div>`);
  if (account.type_school_principal_name) {
    lines.push(`<div style="color:#475569;font-size:11px;">ATTN ${escapeHtml(account.type_school_principal_name)}</div>`);
  }
  return lines.join('');
}

function lineItemsRows(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return `<tr><td colspan="4" style="padding:18px 0;text-align:center;color:#94a3b8;font-style:italic;">No line items</td></tr>`;
  }
  return items.map((li, idx) => `
    <tr style="background:${idx % 2 === 0 ? '#fafbfc' : '#ffffff'};">
      <td style="padding:9px 12px;border-bottom:1px solid #eef0f3;color:#0a1228;">${escapeHtml(li.description)}</td>
      <td style="padding:9px 12px;border-bottom:1px solid #eef0f3;text-align:right;color:#475569;">${escapeHtml(String(li.qty))}</td>
      <td style="padding:9px 12px;border-bottom:1px solid #eef0f3;text-align:right;color:#475569;">${fmtMoney(li.unit_cost_cents)}</td>
      <td style="padding:9px 12px;border-bottom:1px solid #eef0f3;text-align:right;color:#0a1228;font-weight:600;">${fmtMoney(li.amount_cents)}</td>
    </tr>
  `).join('');
}

function totalsBlock(invoice, items) {
  const subtotal = (items || []).reduce((s, r) => s + (Number(r.amount_cents) || 0), 0);
  const total = invoice.amount_cents != null ? invoice.amount_cents : subtotal;
  const exempt = invoice.tax_exempt === 1;
  return `
    <div style="display:flex;justify-content:flex-end;margin-top:12px;">
      <div style="min-width:280px;">
        <div style="display:flex;justify-content:space-between;padding:5px 0;color:#475569;font-size:12px;">
          <span style="text-transform:uppercase;letter-spacing:0.06em;font-size:9px;color:#94a3b8;">Subtotal</span>
          <span style="font-variant-numeric:tabular-nums;">${fmtMoney(subtotal, invoice.currency)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:5px 0;color:#475569;font-size:12px;">
          <span style="text-transform:uppercase;letter-spacing:0.06em;font-size:9px;color:#94a3b8;">Tax rate</span>
          <span style="${exempt ? 'color:#9B50E0;font-weight:600;letter-spacing:0.04em;' : ''}">${fmtTaxRate(invoice)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:5px 0 10px;color:#475569;font-size:12px;border-bottom:1px solid #e5e7eb;">
          <span style="text-transform:uppercase;letter-spacing:0.06em;font-size:9px;color:#94a3b8;">Tax</span>
          <span style="${exempt ? 'color:#9B50E0;font-weight:600;letter-spacing:0.04em;' : ''}">${fmtTaxAmount(invoice)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:baseline;padding:10px 0 4px;border-top:2px solid #0a1228;margin-top:-1px;">
          <span style="text-transform:uppercase;letter-spacing:0.08em;font-size:10px;font-weight:700;color:#0a1228;">Total due</span>
          <span style="font-size:20px;font-weight:700;color:#0a1228;font-variant-numeric:tabular-nums;">${fmtMoney(total, invoice.currency)}</span>
        </div>
      </div>
    </div>
  `;
}

function notesBlock(notes) {
  if (!notes) return '';
  return `
    <div style="margin-top:18px;padding:10px 14px;background:#faf6ff;border-left:3px solid #9B50E0;border-radius:0 4px 4px 0;font-size:11px;color:#334155;line-height:1.5;">
      <div style="font-size:9px;text-transform:uppercase;letter-spacing:0.08em;color:#9B50E0;font-weight:700;margin-bottom:4px;">Notes</div>
      ${escapeHtml(notes).replace(/\n/g, '<br/>')}
    </div>
  `;
}

function taxExemptBlock(invoice) {
  if (invoice.tax_exempt !== 1) return '';
  return `
    <div style="margin-top:12px;padding:9px 14px;background:#f6fce6;border:1px solid #d3eea1;border-radius:4px;font-size:10px;color:#3d5818;line-height:1.5;">
      <strong style="color:#2a4010;letter-spacing:0.04em;">NYC DOE TAX EXEMPT</strong> — ${NYC_DOE_TAX_EXEMPT_NOTE}
    </div>
  `;
}

function signatureBlock(invoice, signature) {
  if (invoice.signature_required !== 1) return '';
  if (signature) {
    const isTyped = signature.signature_type === 'typed';
    const sigContent = isTyped
      ? `<div style="font-family:'${escapeHtml(signature.font_family || 'Dancing Script')}', cursive;font-size:30px;color:#0a1228;line-height:1.1;">${escapeHtml(signature.signature_data || '')}</div>`
      : `<img src="${escapeHtml(signature.signature_data || '')}" alt="signature" style="max-height:64px;max-width:240px;display:block;" />`;
    return `
      <div style="margin-top:18px;padding:14px 16px;border:2px solid #B0FC38;background:#f7ffe6;border-radius:6px;">
        <div style="font-size:9px;text-transform:uppercase;letter-spacing:0.08em;color:#3d7a00;font-weight:700;margin-bottom:6px;">Signed</div>
        ${sigContent}
        <div style="margin-top:6px;font-size:10px;color:#3d5818;">
          Signed on ${escapeHtml(fmtDate(signature.signed_at))}
        </div>
      </div>
    `;
  }
  return `
    <div style="margin-top:18px;padding:14px 16px;border:1px dashed #cbd5e1;border-radius:6px;">
      <div style="font-size:9px;text-transform:uppercase;letter-spacing:0.08em;color:#475569;font-weight:700;margin-bottom:8px;">Signature required</div>
      <ol style="margin:0 0 14px 18px;padding:0;font-size:11px;color:#475569;line-height:1.5;">
        <li>I confirm the information on this invoice is accurate.</li>
        <li>I have authority to sign on behalf of the school.</li>
        <li>I agree to the NexSav service terms referenced above.</li>
      </ol>
      <!-- Real ink/typed signature lands in the empty 70px box below before
           the line. Two columns: signature on the left, name + date on the
           right. Keeps the print-ready layout balanced. -->
      <div style="display:flex;gap:32px;margin-top:8px;">
        <div style="flex:1;">
          <div style="height:70px;border-bottom:1px solid #475569;"></div>
          <div style="margin-top:6px;font-size:10px;color:#475569;letter-spacing:0.04em;text-transform:uppercase;font-weight:600;">Authorized signature</div>
        </div>
        <div style="flex:1;">
          <div style="height:70px;border-bottom:1px solid #475569;display:flex;align-items:flex-end;padding-bottom:4px;">
            <span style="font-size:10px;color:#94a3b8;font-style:italic;">Print name &amp; date</span>
          </div>
          <div style="margin-top:6px;font-size:10px;color:#475569;letter-spacing:0.04em;text-transform:uppercase;font-weight:600;">Name &amp; date</div>
        </div>
      </div>
    </div>
  `;
}

function metaRow(label, value) {
  return `
    <div style="display:flex;justify-content:space-between;align-items:baseline;padding:4px 0;border-bottom:1px solid #f1f5f9;">
      <span style="font-size:9px;text-transform:uppercase;letter-spacing:0.08em;color:#94a3b8;font-weight:600;">${label}</span>
      <span style="font-size:12px;color:#0a1228;font-variant-numeric:tabular-nums;">${value}</span>
    </div>
  `;
}

export function renderInvoiceHtml(invoice, lineItems = [], account = {}, signature = null) {
  if (!invoice) throw new Error('renderInvoiceHtml: invoice is required');

  const invoiceNum = escapeHtml(invoice.invoice_number || '');
  const meta = [
    metaRow('Invoice #', invoiceNum || '—'),
    metaRow('Date of issue', escapeHtml(fmtDate(invoice.issued_at))),
    metaRow('Valid through', escapeHtml(fmtDate(invoice.valid_through))),
    invoice.po_number ? metaRow('PO Number', escapeHtml(invoice.po_number)) : '',
  ].join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Invoice ${invoiceNum}</title>
  <style>
    /* margin:0 on @page suppresses the browser-imposed URL/timestamp
       header+footer that the print dialog adds by default. We re-create
       page padding via .sheet so content still has comfortable margins. */
    @page { size: Letter; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: -apple-system, "SF Pro Text", "Segoe UI", system-ui, Roboto, sans-serif;
      color: #0a1228;
      background: #ffffff;
      font-size: 12px;
      line-height: 1.45;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    .sheet {
      max-width: 760px;
      margin: 0 auto;
      padding: 28px 32px;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 20px;
      padding-bottom: 18px;
      border-bottom: 1px solid #e5e7eb;
    }
    .brand img {
      width: 64px;
      height: 64px;
      object-fit: contain;
      display: block;
    }
    .brand .company {
      margin-top: 10px;
      font-size: 11px;
      color: #475569;
      line-height: 1.4;
    }
    .brand .company strong { color: #0a1228; }
    .invoice-badge { text-align: right; }
    .invoice-badge .word {
      font-size: 26px;
      font-weight: 800;
      letter-spacing: -0.02em;
      color: #0a1228;
      line-height: 1;
      margin-bottom: 4px;
    }
    .invoice-badge .num {
      font-size: 12px;
      color: #9B50E0;
      font-weight: 600;
      letter-spacing: 0.04em;
      font-variant-numeric: tabular-nums;
    }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 28px;
      margin: 18px 0;
    }
    .grid h4 {
      margin: 0 0 8px;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: #94a3b8;
      font-weight: 700;
    }
    table.items {
      width: 100%;
      border-collapse: collapse;
      margin-top: 14px;
      font-size: 12px;
    }
    table.items thead th {
      text-align: left;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #94a3b8;
      font-weight: 700;
      padding: 8px 12px;
      border-bottom: 2px solid #0a1228;
      background: #ffffff;
    }
    table.items thead th.num { text-align: right; }
    .footer {
      margin-top: 28px;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 10px;
      color: #94a3b8;
      letter-spacing: 0.04em;
    }
    .footer .accent {
      color: #9B50E0;
      font-weight: 600;
    }
    @media print {
      html, body { width: 8.5in; }
      .sheet { padding: 18px 24px; max-width: none; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="header">
      <div class="brand">
        <img src="${NEXSAV_LOGO_PNG_DATA_URL}" alt="NexSav Innovate"
             style="width:64px;height:64px;object-fit:contain;display:block;" />
        <div class="company">
          <strong>${escapeHtml(COMPANY_NAME)}</strong><br/>
          ${COMPANY_ADDRESS_LINES.map((l) => escapeHtml(l)).join('<br/>')}<br/>
          ${escapeHtml(COMPANY_TAGLINE)}
        </div>
      </div>
      <div class="invoice-badge">
        <div class="word">INVOICE</div>
        <div class="num">${invoiceNum || '—'}</div>
      </div>
    </div>

    <div class="grid">
      <div>
        <h4>Billed to</h4>
        ${billedToBlock(account)}
      </div>
      <div>
        <h4>Details</h4>
        ${meta}
      </div>
    </div>

    <table class="items">
      <thead>
        <tr>
          <th style="width:55%;">Description</th>
          <th class="num" style="width:10%;">Qty</th>
          <th class="num" style="width:17%;">Unit cost</th>
          <th class="num" style="width:18%;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${lineItemsRows(lineItems)}
      </tbody>
    </table>

    ${totalsBlock(invoice, lineItems)}

    ${notesBlock(invoice.notes_client)}

    ${taxExemptBlock(invoice)}

    ${signatureBlock(invoice, signature)}

    <div class="footer">
      <div>${escapeHtml(COMPANY_NAME)} · ${escapeHtml(COMPANY_ADDRESS_INLINE)} · Vendor # ${escapeHtml(VENDOR_NUMBER)}</div>
      <div class="accent">Thank you for partnering with NexSav Innovate.</div>
    </div>
  </div>
</body>
</html>`;
}
